// One onsite, both stages.
//
// The request on the left, the completed on-site summary on the right,
// which a clinic files on its own jobs and staff file on any. Onsites are
// operational record only: spend comes from the monthly activity import, so
// there is no finalize step and no money rolls out of here. The checklist
// mirrors the server's completion gate. The mileage helper multiplies miles
// by the current system rate in front of the user, for reference only.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getOnsite,
  scheduleOnsite,
  completeOnsite,
  cancelOnsite,
  removeOnsite,
  resendDispatchEmail,
  saveOnsiteSummary,
  uploadOnsiteDocument,
  onsiteDocumentUrl,
  removeOnsiteDocument,
} from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  DateField,
  Detail,
  EmptyState,
  FilePicker,
  Input,
  Notice,
  Panel,
  Pill,
} from '../components/ui/index.js'
import { onsiteStatusTone } from '../tones/onsites.js'
import { useConfirm } from '../confirm.jsx'
import { useApiForm } from '../useApiForm.js'
import {
  onsiteStatusLabel,
  formatPrice,
  formatInstant,
  fromInstant,
  formatBytes,
  SEPARATOR,
} from '../vocab.js'

// Stage 2, the completed on-site summary: the actuals the gate requires, the
// collectors, the fees that never block, notes, and the paperwork. Whoever
// saves it is recorded as the reporter; a clinic files its own jobs here.
function SummaryCard({ onsite, onSaved }) {
  const { user } = useAuth()
  const confirm = useConfirm()
  const completed = fromInstant(onsite.completedAt)
  const [form, setForm] = useState({
    actualDonorCount: onsite.actualDonorCount ?? '',
    actualNoShowCount: onsite.actualNoShowCount ?? '',
    completedDate: completed.date,
    completedTime: completed.time,
    collectorCount: onsite.collectorCount ?? '',
    tolls: onsite.tolls ?? '',
    parkingFees: onsite.parkingFees ?? '',
    onsiteFee: onsite.onsiteFee ?? '',
    hourlyRate: onsite.hourlyRate ?? '',
    hours: onsite.hours ?? '',
    mileageMiles: onsite.mileageMiles ?? '',
    notes: onsite.notes ?? '',
  })
  const [document, setDocument] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [documentError, setDocumentError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function save() {
    // A blank box means "not recorded"; counts go as numbers, fees as typed.
    const orNull = (value) => (value === '' ? null : value)
    const count = (value) => (value === '' ? null : Number(value))
    const payload = {
      actualDonorCount: count(form.actualDonorCount),
      actualNoShowCount: count(form.actualNoShowCount),
      completedAt: form.completedDate
        ? new Date(`${form.completedDate}T${form.completedTime || '17:00'}`).toISOString()
        : null,
      collectorCount: count(form.collectorCount),
      tolls: orNull(form.tolls),
      parkingFees: orNull(form.parkingFees),
      onsiteFee: orNull(form.onsiteFee),
      hourlyRate: orNull(form.hourlyRate),
      hours: orNull(form.hours),
      mileageMiles: orNull(form.mileageMiles),
      notes: form.notes || null,
    }
    const result = await submit(() => saveOnsiteSummary(onsite.id, payload))
    if (result) onSaved()
  }

  async function upload() {
    if (!document) return
    setUploading(true)
    setDocumentError(null)
    try {
      await uploadOnsiteDocument(onsite.id, document)
      setDocument(null)
      onSaved()
    } catch (caught) {
      setDocumentError(caught.message)
    } finally {
      setUploading(false)
    }
  }

  async function drop(row) {
    if (!(await confirm(`Remove ${row.fileName} from this onsite?`))) return
    setDocumentError(null)
    try {
      await removeOnsiteDocument(onsite.id, row.id)
      onSaved()
    } catch (caught) {
      setDocumentError(caught.message)
    }
  }

  // The mileage helper: today's rate, display only.
  const rate = onsite.currentMileageRate
  const miles = Number(form.mileageMiles)
  const mileageAmount = rate && miles > 0 ? miles * Number(rate) : null
  const labor =
    form.hourlyRate !== '' && form.hours !== ''
      ? Number(form.hourlyRate) * Number(form.hours)
      : null

  const numberField = (name, label, help) => (
    <Input
      id={`job-${name}`}
      label={label}
      type="number"
      min="0"
      step={name.includes('Count') ? '1' : '0.01'}
      value={form[name]}
      onChange={(event) => change(name, event.target.value)}
      error={fieldErrors[name]}
      help={help}
    />
  )

  // Whoever saves is the reporter, so a correction by somebody else says so
  // before it happens.
  const me = user?.displayName || user?.email
  const stored = onsite.reportedByName
    ? [
        onsite.reportedByName,
        onsite.reportedByEmail !== onsite.reportedByName && onsite.reportedByEmail,
      ]
        .filter(Boolean)
        .join(SEPARATOR)
    : null
  const reporter = !stored
    ? `Recorded as ${me} when you save.`
    : onsite.reportedByEmail === user?.email
      ? stored
      : `${stored}. Saving records it as ${me}.`

  return (
    <Panel
      title="Completed on-site summary"
      support="What happened on the job. The three actuals gate completion; nothing else here does. Leave blank whatever was not charged."
    >
      {error ? <Notice tone="bad">{error}</Notice> : null}

      <Detail label="Reported by" wraps>
        {reporter}
      </Detail>

      <div className="formgrid">
        {numberField('actualDonorCount', 'Actual donors')}
        {numberField('actualNoShowCount', 'No shows')}
        <DateField
          id="completed-date"
          label="Date of service"
          value={form.completedDate}
          onChange={(value) => change('completedDate', value)}
        />
        <Input
          id="job-completedTime"
          label="Completed at"
          type="time"
          value={form.completedTime}
          onChange={(event) => change('completedTime', event.target.value)}
          help="Defaults to 17:00 if left blank."
        />
        {numberField('collectorCount', 'Collectors on site')}

        {numberField('tolls', 'Toll fees')}
        {numberField('parkingFees', 'Parking fees')}
        {numberField('onsiteFee', 'Onsite fee')}
        {numberField('hourlyRate', 'Hourly rate')}
        {numberField(
          'hours',
          'Hours',
          labor !== null ? `Labor charge ${formatPrice(labor)}` : null,
        )}
        {numberField(
          'mileageMiles',
          'Miles driven',
          rate
            ? `At ${formatPrice(rate)} per mile${
                mileageAmount !== null ? `: ${formatPrice(mileageAmount)}` : ''
              }, the current rate, for reference only`
            : null,
        )}

        <Input
          id="job-notes"
          className="span-2"
          label="Additional notes"
          multiline
          value={form.notes}
          onChange={(event) => change('notes', event.target.value)}
        />
      </div>

      <div className="btnrow">
        <span className="spacer" />
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? 'Saving' : 'Save summary'}
        </Button>
      </div>

      <div className="form-section">
        <h2>Documents</h2>
        <p className="support">
          The paperwork from the job: a CSV, PDF, JPEG or PNG, up to 10 MB each.
        </p>
      </div>

      {documentError ? <Notice tone="bad">{documentError}</Notice> : null}

      {onsite.documents.length ? (
        <div className="rows">
          {onsite.documents.map((row) => (
            <div key={row.id} className="row">
              <div className="row-main">
                <div className="row-primary">
                  <a href={onsiteDocumentUrl(onsite.id, row.id)}>{row.fileName}</a>
                </div>
                <div className="row-meta">
                  {[formatBytes(row.byteSize), row.uploadedBy, formatInstant(row.createdAt)]
                    .filter(Boolean)
                    .join(SEPARATOR)}
                </div>
              </div>
              <div className="row-trail">
                <Button variant="ghost" onClick={() => drop(row)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="card-note muted">Nothing attached yet.</p>
      )}

      <div className="btnrow">
        <FilePicker
          id="job-document"
          file={document}
          accept=".csv,.pdf,.jpg,.jpeg,.png"
          onChange={(file) => {
            setDocumentError(null)
            setDocument(file)
          }}
          chooseLabel="Choose a document"
        />
        <span className="spacer" />
        <Button onClick={upload} disabled={!document || uploading}>
          {uploading ? 'Attaching' : 'Attach'}
        </Button>
      </div>
    </Panel>
  )
}

// The completion checklist: what the server still needs before the job can
// be marked completed. Mirrors missingActuals from the API.
function ChecklistCard({ onsite }) {
  if (!onsite.missingActuals.length) return null
  return (
    <Panel
      title="Before completion"
      support="The completed on-site summary needs these before the job can be marked completed."
    >
      <div className="rows">
        {onsite.missingActuals.map((item) => (
          <div key={item} className="row">
            <div className="row-main">
              <div className="row-primary wraps">{item}</div>
              <div className="row-meta">Record it in the summary</div>
            </div>
            <div className="row-trail">
              <Pill tone="warn">Needed</Pill>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  )
}

export default function OnsiteDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const confirm = useConfirm()
  const canWrite = can(ACTION.ONSITES_WRITE)
  const canReport = can(ACTION.ONSITES_REPORT)
  const canDelete = can(ACTION.RECORDS_DELETE)
  const [onsite, setOnsite] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [actionError, setActionError] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await getOnsite(id)
      setOnsite(data.onsite)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  // Every toolbar action: confirm if asked, run, then reload (or whatever
  // comes next), with a failure shown above the page.
  async function act(request, message, then = load) {
    if (message && !(await confirm(message.text, message.options))) return
    setActionError(null)
    try {
      await request()
      then()
    } catch (caught) {
      setActionError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!onsite) return <div className="center-note">Loading</div>

  const open = onsite.status !== 'CANCELED'

  return (
    <>
      <header className="page-head">
        <h1>Onsite for {onsite.customerName}</h1>
        <p className="support">
          <Pill tone={onsiteStatusTone[onsite.status]}>{onsiteStatusLabel(onsite.status)}</Pill>{' '}
          {[onsite.siteName, formatInstant(onsite.requestedAt)].filter(Boolean).join(SEPARATOR)}
        </p>
      </header>

      {actionError ? <Notice tone="bad">{actionError}</Notice> : null}

      <div className="toolbar">
        <ButtonLink to="/onsites">Back to the queue</ButtonLink>
        {onsite.clickupUrl ? (
          <a
            className="ui-btn ui-btn-secondary"
            href={onsite.clickupUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open in ClickUp
          </a>
        ) : null}
        <span className="spacer" />
        {canDelete ? (
          <Button
            variant="ghost"
            onClick={() =>
              act(
                () => removeOnsite(id),
                {
                  text: 'Remove this onsite entirely? Cancel is the usual way to close a request; removing erases it from every screen.',
                  options: { title: 'Remove the onsite', confirmLabel: 'Remove onsite' },
                },
                () => navigate('/onsites'),
              )
            }
          >
            Remove
          </Button>
        ) : null}
        {canWrite && open && onsite.franchiseeId ? (
          <Button variant="ghost" onClick={() => act(() => resendDispatchEmail(id))}>
            {onsite.dispatchEmail?.sentAt ? 'Resend dispatch email' : 'Send dispatch email'}
          </Button>
        ) : null}
        {canWrite && open ? (
          <>
            <Button
              variant="ghost"
              onClick={() =>
                act(() => cancelOnsite(id), {
                  text: 'Cancel this onsite? The record is kept with the reason.',
                  options: { title: 'Cancel the onsite', confirmLabel: 'Cancel onsite' },
                })
              }
            >
              Cancel onsite
            </Button>
            {onsite.status === 'REQUESTED' ? (
              <Button onClick={() => act(() => scheduleOnsite(id))}>Mark scheduled</Button>
            ) : null}
            {onsite.status !== 'COMPLETED' ? (
              <Button onClick={() => act(() => completeOnsite(id))}>Mark completed</Button>
            ) : null}
            <ButtonLink variant="primary" to={`/onsites/${id}/edit`}>
              Edit request
            </ButtonLink>
          </>
        ) : null}
      </div>

      <div className="split">
        <div className="stack">
          <Panel title="The request">
            <div className="rows">
              {onsite.submittedThrough ? (
                <Detail wraps label="Arrived through">
                  {`Submission link: ${onsite.submittedThrough}`}
                </Detail>
              ) : null}
              <Detail wraps label="Who called">
                {[onsite.requestingContactName, onsite.requestingContactPhone]
                  .filter(Boolean)
                  .join(SEPARATOR) || null}
              </Detail>
              <Detail wraps label="Requested for">
                {formatInstant(onsite.requestedAt)}
              </Detail>
              <Detail wraps label="Site">
                {[onsite.siteName, onsite.siteAddress].filter(Boolean).join(SEPARATOR) || null}
              </Detail>
              <Detail wraps label="Dispatched to">
                {onsite.franchiseeName}
              </Detail>
              <Detail wraps label="Dispatch email">
                {onsite.dispatchEmail?.sentAt
                  ? `Sent to ${onsite.dispatchEmail.to}${SEPARATOR}${formatInstant(onsite.dispatchEmail.sentAt)}`
                  : onsite.dispatchEmail?.error
                    ? `Not sent: ${onsite.dispatchEmail.error}`
                    : onsite.franchiseeId
                      ? 'Not sent yet'
                      : null}
              </Detail>
              <Detail wraps label="Franchise ID">
                {onsite.franchiseeFranchiseId}
              </Detail>
              <Detail wraps label="Collector home base">
                {onsite.collectorHomeBaseAddress}
              </Detail>
              <Detail wraps label="Estimated donors">
                {onsite.estimatedDonorCount}
              </Detail>
            </div>
            {onsite.instructions ? <p className="card-note muted">{onsite.instructions}</p> : null}
          </Panel>

          <Panel title="Requested tests" support="Estimates from the call.">
            {onsite.requestedTests.length ? (
              <div className="rows">
                {onsite.requestedTests.map((line) => (
                  <div key={line.testTypeId} className="row">
                    <div className="row-main">
                      <div className="row-primary">{line.name}</div>
                    </div>
                    <div className="row-trail">
                      {line.estimatedQuantity ?? <span className="muted">No estimate</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="None listed">The call did not name specific tests.</EmptyState>
            )}
          </Panel>

          {onsite.status === 'CANCELED' ? (
            <Notice tone="bad" role="status">
              Canceled {formatInstant(onsite.canceledAt)}
              {onsite.cancelReason ? `: ${onsite.cancelReason}` : '.'}
            </Notice>
          ) : null}
        </div>

        <div className="stack">
          {canReport && open ? <SummaryCard onsite={onsite} onSaved={load} /> : null}
          {open ? <ChecklistCard onsite={onsite} /> : null}
        </div>
      </div>
    </>
  )
}
